import React, { forwardRef, useCallback, useMemo, useState } from 'react';
import { useColors } from '../../contexts/ColorContext';
import clsx from 'clsx';

interface Material3CardProps {
  variant?: 'elevated' | 'filled' | 'outlined';
  children: React.ReactNode;
  onClick?: () => void;
  interactive?: boolean;
  className?: string;
  style?: React.CSSProperties;
  shape?: string;
  marineTheme?: boolean;
  'aria-label'?: string;
  role?: string;
  tabIndex?: number;
  disabled?: boolean;
}

export const Material3Card = forwardRef<HTMLDivElement, Material3CardProps>(
  (
    {
      variant = 'elevated',
      children,
      onClick,
      interactive = false,
      className,
      style,
      shape: _shape,
      marineTheme: _marineTheme,
      'aria-label': ariaLabel,
      role,
      tabIndex,
      disabled = false,
      ...rest
    },
    ref
  ) => {
    const { dynamicColors } = useColors();
    const [isHovered, setIsHovered] = useState(false);
    const [isPressed, setIsPressed] = useState(false);

    const handleMouseEnter = useCallback(() => {
      if (!disabled) setIsHovered(true);
    }, [disabled]);

    const handleMouseLeave = useCallback(() => {
      setIsHovered(false);
      setIsPressed(false);
    }, []);

    const handleMouseDown = useCallback(() => {
      if (!disabled && (interactive || onClick)) {
        setIsPressed(true);
      }
    }, [disabled, interactive, onClick]);

    const handleMouseUp = useCallback(() => {
      setIsPressed(false);
    }, []);

    const handleClick = useCallback(() => {
      if (!disabled && onClick) {
        onClick();
      }
    }, [disabled, onClick]);

    const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
      if (!disabled && onClick && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        onClick();
      }
    }, [disabled, onClick]);

    const cardStyles = useMemo(() => {
      const isInteractive = Boolean(interactive || onClick);
      const baseStyles: React.CSSProperties = {
        borderRadius: '24px',
        transition: 'transform 140ms ease-out, box-shadow 160ms ease-out, border-color 140ms ease-out, background-color 140ms ease-out',
        cursor: onClick && !disabled ? 'pointer' : 'default',
        position: 'relative',
        overflow: 'hidden',
        transform: isPressed ? 'translateY(1px)' : isHovered && isInteractive && !disabled ? 'translateY(-1px)' : 'translateY(0)',
      };

      switch (variant) {
        case 'filled':
          return {
            ...baseStyles,
            backgroundColor: dynamicColors.surfaceVariant,
            border: 'none',
            boxShadow: '0 1px 2px rgba(3, 4, 94, 0.05)',
          };
        case 'outlined':
          return {
            ...baseStyles,
            backgroundColor: dynamicColors.surface,
            border: `1px solid ${isHovered ? `${dynamicColors.primary}40` : dynamicColors.outline}`,
            boxShadow: 'none',
          };
        case 'elevated':
        default:
          return {
            ...baseStyles,
            backgroundColor: dynamicColors.surface,
            border: `1px solid ${dynamicColors.outline}`,
            boxShadow: isHovered && isInteractive ? '0 8px 18px rgba(3, 4, 94, 0.08)' : '0 4px 10px rgba(3, 4, 94, 0.05)',
          };
      }
    }, [variant, dynamicColors, isHovered, isPressed, interactive, onClick, disabled]);

    return (
      <div
        ref={ref}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        role={role || (onClick && !disabled ? 'button' : undefined)}
        tabIndex={tabIndex !== undefined ? tabIndex : (onClick && !disabled ? 0 : undefined)}
        aria-label={ariaLabel}
        className={clsx('focus:outline-none focus:ring-2 focus:ring-offset-2', onClick && !disabled && 'focus:ring-2', disabled && 'cursor-not-allowed opacity-50', className)}
        style={{
          ...cardStyles,
          color: dynamicColors.onSurface,
          ...style,
          ...(onClick && !disabled ? { '--tw-ring-color': dynamicColors.primary } : {}),
        } as React.CSSProperties}
        {...rest}
      >
        {children}
      </div>
    );
  }
);

Material3Card.displayName = 'Material3Card';

export default Material3Card;
